'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import Script from 'next/script';
import { useEffect } from 'react';
import { FB_PIXEL_ID } from './fpixel';
import * as gtag from './gtag';
import * as fpixel from './fpixel';
import { GA4_STREAM_ID, GA4_MEASUREMENT_ID, GOOGLE_ADS_CONVERSION_ID } from './ga4';
import { getConsentData } from '@/components/cookie-modal';
import { VercelAnalytics } from './vercel';
export function AnalyticsScripts() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const consentData = getConsentData();

      // Set consent state based on user preferences
      if (consentData) {
        // First set default consent state (all denied)
        gtag.setDefaultConsentState();

        // Then update based on user preferences
        gtag.updateConsentState({
          analyticsStorage: consentData.measurement ? 'granted' : 'denied',
          adStorage: consentData.marketing ? 'granted' : 'denied',
          adUserData: consentData.marketing ? 'granted' : 'denied',
          adPersonalization: consentData.marketing ? 'granted' : 'denied'
        });

        // Track pageview if measurement consent given
        if (consentData.measurement) {
          // Small delay to ensure consent is processed first
          setTimeout(() => {
            // Use pathname + search for tracking instead of full URL
            const pathname = window.location.pathname;
            const search = window.location.search;
            gtag.pageview(pathname + search);
            console.log('Tracked pageview for', pathname + search);
          }, 100);
        }
      }
    }
  }, [pathname, searchParams]);
  const consentData = getConsentData();

  // Always include Vercel Analytics, as it's privacy-friendly
  // Then conditionally add other scripts based on consent
  return <>
      {/* Vercel Analytics - always included regardless of consent */}
      <VercelAnalytics data-sentry-element="VercelAnalytics" data-sentry-source-file="scripts.tsx" />

      {/* Only load consent-requiring scripts if consent data exists */}
      {consentData && <>
          {/* Google Analytics - initialize with consent mode */}
          <Script src={`https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`} strategy="afterInteractive" />
          <Script id="google-analytics" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              
              // Enable URL passthrough
              gtag('set', 'url_passthrough', true);
              
              // Initialize GA with config - this will be the initial pageview
              // The useEffect hook will handle consent and subsequent pageviews
              gtag('config', '${GA4_MEASUREMENT_ID}', {
                stream_id: '${GA4_STREAM_ID}'
              });
              
              // Add Google Ads configuration
              gtag('config', '${GOOGLE_ADS_CONVERSION_ID}');
              
              console.log('Google Analytics initialized with Measurement ID: ${GA4_MEASUREMENT_ID} and Stream ID: ${GA4_STREAM_ID}');
            `}
          </Script>

          {/* Meta Pixel - always load with consent configuration */}
          <Script id="facebook-pixel" strategy="afterInteractive">
            {`
              // Global type definition for fbq
              window.fbPixelInitialized = false;
              
              // Only initialize Meta Pixel once, but ALWAYS init with consent mechanism
              if (!window.fbPixelInitialized) {
                window.fbPixelInitialized = true;
                
                // Standard Meta Pixel initialization
                !function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                
                // Initialize pixel with consent mode enabled
                fbq('init', '${FB_PIXEL_ID}', {
                  external_id: (window.localStorage.getItem('vercel_analytics_sid') || '').substr(0, 20)
                });
                
                // CRITICAL: Configure data processing options for GDPR compliance
                // This enables Limited Data Use (LDU) mode
                fbq('dataProcessingOptions', ['LDU'], 1, 0);
                
                // CRITICAL: Disable automatic PageView tracking
                fbq('set', 'autoConfig', false, '${FB_PIXEL_ID}');
                
                // Set consent state based on user preferences
                // Default to NO consent until explicitly granted
                fbq('consent', 'revoke');
                
                // If user has given marketing consent, grant it
                const consentData = ${JSON.stringify(consentData)};
                if (consentData && consentData.marketing) {
                  fbq('consent', 'grant');
                  console.log('Facebook Pixel consent granted');
                } else {
                  console.log('Facebook Pixel consent revoked or not provided');
                }
                
                // Set additional parameters required by Meta
                fbq('set', {
                  'eventSourceUrl': window.location.href,
                  'userData': {
                    'region': 'europe',
                    'country': 'italy'
                  }
                });
                
                console.log('Facebook Pixel initialized with enhanced privacy settings');
              }
            `}
          </Script>
        </>}
    </>;
}